export const DEFAULT_SVANTEK_CUSTOM_CONFIG =
    "#1,M3,XA1440,XD-420,Y0,y15,D5m,K0,T1,F2:1,J3:1,C1:1,F2:2,J3:2,C0:2,F3:3,J3:3,C1:3,f1,B15:1,B8:2,B15:3,b8,d1s,L1,XXu0;";

type SupportedTokenKey = "IB" | "MC" | "FF" | "XXu" | "XXm" | "XXn" | "XXl" | "XXo" | "XXx" | "XXv" | "XXY" | "XXZ" | "XXz" | "XXy" | "XXq";

type LengthLimitMode = "4gb" | "minutes";

interface ParsedToken {
    raw: string;
    key: SupportedTokenKey | null;
    isPinned: boolean;
    isVisualControl: boolean;
}

export interface SvantekDiffTokenSegment {
    token: string;
    isChanged: boolean;
}

export interface SvantekVisualConfigState {
    waveFileName: string;
    microphoneCompensation: "" | "0" | "1";
    freeFieldCompensation: "" | "0" | "1" | "2";
    waveRecordingMode: "0" | "1" | "2" | "3" | "4" | "5" | "6" | "7" | "8" | "10" | "11";
    format: "0" | "1" | "2";
    bitsPerSample: "16" | "24";
    recordingTime: number;
    preTrigger: number;
    recordingFilter: "1" | "2" | "3" | "5";
    samplingRate: "0" | "1" | "2" | "3";
    gain: number;
    triggerLevel: number;
    triggerPeriod: "0" | "5" | "1000" | "10000";
    triggerGradient: number;
    lengthLimitMode: LengthLimitMode;
    lengthLimitMinutes: number | null;
}

export interface ParsedSvantekCustomConfig {
    rawInput: string | null;
    effectiveConfig: string;
    isValid: boolean;
    hasPinnedDifferences: boolean;
    supported: SvantekVisualConfigState;
    tokens: ParsedToken[];
    supportedInsertIndex: number;
}

const supportedPrefixes: SupportedTokenKey[] = [
    "IB",
    "MC",
    "FF",
    "XXu",
    "XXm",
    "XXn",
    "XXl",
    "XXo",
    "XXx",
    "XXv",
    "XXY",
    "XXZ",
    "XXz",
    "XXy",
    "XXq",
];
const pinnedTokens = [
    "M3",
    "XA1440",
    "XD-420",
    "Y0",
    "y15",
    "D5m",
    "K0",
    "T1",
    "F2:1",
    "J3:1",
    "C1:1",
    "F2:2",
    "J3:2",
    "C0:2",
    "F3:3",
    "J3:3",
    "C1:3",
    "f1",
    "B15:1",
    "B8:2",
    "B15:3",
    "b8",
    "d1s",
    "L1",
];

const defaultVisualConfig: SvantekVisualConfigState = {
    waveFileName: "",
    microphoneCompensation: "",
    freeFieldCompensation: "",
    waveRecordingMode: "0",
    format: "0",
    bitsPerSample: "16",
    recordingTime: 60,
    preTrigger: 1,
    recordingFilter: "1",
    samplingRate: "0",
    gain: 0,
    triggerLevel: 100,
    triggerPeriod: "0",
    triggerGradient: 10,
    lengthLimitMode: "4gb",
    lengthLimitMinutes: null,
};

const supportedTokenPattern: Record<SupportedTokenKey, RegExp> = {
    IB: /^IB([A-Z0-9_]{1,8})$/,
    MC: /^MC(0|1)$/,
    FF: /^FF(0|1|2)$/,
    XXu: /^XXu(0|1|2|3|4|5|6|7|8|10|11)$/,
    XXm: /^XXm(0|1|2)$/,
    XXn: /^XXn(16|24)$/,
    XXl: /^XXl(0|1|2|3)$/,
    XXo: /^XXo(1|2|3|5)$/,
    XXx: /^XXx(\d+)$/,
    XXv: /^XXv(\d+)$/,
    XXY: /^XXY(0|5|1000|10000)$/,
    XXZ: /^XXZ(\d+)$/,
    XXy: /^XXy(\d+)$/,
    XXz: /^XXz(\d+)$/,
    XXq: /^XXq(\d+)$/,
};

export function getDefaultSvantekVisualConfig(): SvantekVisualConfigState {
    return { ...defaultVisualConfig };
}

export function parseSvantekCustomConfig(rawInput?: string | null): ParsedSvantekCustomConfig {
    const normalizedInput = normalizeRawConfig(rawInput);
    const rawToParse = normalizedInput ?? DEFAULT_SVANTEK_CUSTOM_CONFIG;
    const isValid = normalizedInput ? isSvantekConfigShapeValid(normalizedInput) : true;
    const parsedTokens = tokenizeSvantekConfig(isValid ? rawToParse : DEFAULT_SVANTEK_CUSTOM_CONFIG);
    const supported = parsedTokens.reduce<SvantekVisualConfigState>((acc, token) => {
        if (!token.key) {
            return acc;
        }

        const match = token.raw.match(supportedTokenPattern[token.key]);
        if (!match) {
            return acc;
        }

        switch (token.key) {
            case "IB":
                acc.waveFileName = match[1];
                break;
            case "MC":
                acc.microphoneCompensation = match[1] as SvantekVisualConfigState["microphoneCompensation"];
                break;
            case "FF":
                acc.freeFieldCompensation = match[1] as SvantekVisualConfigState["freeFieldCompensation"];
                break;
            case "XXu":
                acc.waveRecordingMode = match[1] as SvantekVisualConfigState["waveRecordingMode"];
                break;
            case "XXm":
                acc.format = match[1] as SvantekVisualConfigState["format"];
                break;
            case "XXn":
                acc.bitsPerSample = match[1] as SvantekVisualConfigState["bitsPerSample"];
                break;
            case "XXy":
                acc.recordingTime = Number(match[1]);
                break;
            case "XXz":
                acc.preTrigger = Number(match[1]);
                break;
            case "XXo":
                acc.recordingFilter = match[1] as SvantekVisualConfigState["recordingFilter"];
                break;
            case "XXl":
                acc.samplingRate = match[1] as SvantekVisualConfigState["samplingRate"];
                break;
            case "XXx":
                acc.gain = Number(match[1]);
                break;
            case "XXv":
                acc.triggerLevel = Number(match[1]);
                break;
            case "XXY":
                acc.triggerPeriod = match[1] as SvantekVisualConfigState["triggerPeriod"];
                break;
            case "XXZ":
                acc.triggerGradient = Number(match[1]);
                break;
            case "XXq":
                acc.lengthLimitMode = Number(match[1]) === 0 ? "4gb" : "minutes";
                acc.lengthLimitMinutes = Number(match[1]) === 0 ? null : Number(match[1]);
                break;
        }

        return acc;
    }, getDefaultSvantekVisualConfig());

    const supportedInsertIndex = parsedTokens.findIndex((token) => token.isVisualControl);
    const pinnedSet = new Set(parsedTokens.filter((token) => token.isPinned).map((token) => token.raw));

    return {
        rawInput: normalizedInput,
        effectiveConfig: formatSvantekConfig(parsedTokens.map((token) => token.raw)),
        isValid,
        hasPinnedDifferences: pinnedTokens.some((token) => !pinnedSet.has(token)),
        supported,
        tokens: parsedTokens,
        supportedInsertIndex: supportedInsertIndex >= 0 ? supportedInsertIndex : parsedTokens.length,
    };
}

export function serializeSvantekCustomConfig(builderState: SvantekVisualConfigState, baseConfig: ParsedSvantekCustomConfig): string | null {
    const supportedTokens = buildSupportedTokens(builderState);
    const tokensWithoutSupported = baseConfig.tokens.filter((token) => !token.isVisualControl).map((token) => token.raw);
    const insertIndex = Math.min(baseConfig.supportedInsertIndex, tokensWithoutSupported.length);
    const serializedTokens = [
        ...tokensWithoutSupported.slice(0, insertIndex),
        ...supportedTokens,
        ...tokensWithoutSupported.slice(insertIndex),
    ];
    const nextConfig = formatSvantekConfig(serializedTokens);

    return nextConfig === DEFAULT_SVANTEK_CUSTOM_CONFIG ? null : nextConfig;
}

export function buildSvantekDiffTokenSegments(rawInput?: string | null): SvantekDiffTokenSegment[] {
    const normalizedInput = normalizeRawConfig(rawInput);
    const rawToParse = normalizedInput ?? DEFAULT_SVANTEK_CUSTOM_CONFIG;
    const defaultTokens = new Set(tokenizeSvantekConfig(DEFAULT_SVANTEK_CUSTOM_CONFIG).map((token) => token.raw));

    return tokenizeSvantekConfig(rawToParse).map((token) => ({
        token: token.raw,
        isChanged: !defaultTokens.has(token.raw),
    }));
}

export function buildMissingSvantekDefaultTokens(rawInput?: string | null): string[] {
    const normalizedInput = normalizeRawConfig(rawInput);
    const rawToParse = normalizedInput ?? DEFAULT_SVANTEK_CUSTOM_CONFIG;
    const currentTokens = new Set(tokenizeSvantekConfig(rawToParse).map((token) => token.raw));

    return tokenizeSvantekConfig(DEFAULT_SVANTEK_CUSTOM_CONFIG)
        .map((token) => token.raw)
        .filter((token) => !currentTokens.has(token));
}

function normalizeRawConfig(rawInput?: string | null): string | null {
    if (typeof rawInput !== "string") {
        return null;
    }

    const trimmed = rawInput.trim();
    return trimmed.length ? trimmed : null;
}

function isSvantekConfigShapeValid(rawConfig: string): boolean {
    return /^#1(?:,[^,;]+)*;$/.test(rawConfig);
}

function tokenizeSvantekConfig(rawConfig: string): ParsedToken[] {
    const trimmed = rawConfig.trim();
    const inner = trimmed.replace(/^#1,?/, "").replace(/;$/, "");

    return inner
        .split(",")
        .map((token) => token.trim())
        .filter((token) => token.length > 0)
        .map((token) => {
            const key = supportedPrefixes.find((prefix) => token.startsWith(prefix)) ?? null;
            const isVisualControl = key !== null && supportedTokenPattern[key].test(token);

            return {
                raw: token,
                key,
                isPinned: pinnedTokens.includes(token),
                isVisualControl,
            };
        });
}

function formatSvantekConfig(tokens: string[]): string {
    return `#1,${tokens.join(",")};`;
}

function buildSupportedTokens(builderState: SvantekVisualConfigState): string[] {
    const tokens: string[] = [];

    if (builderState.waveFileName) {
        tokens.push(`IB${builderState.waveFileName.trim().toUpperCase()}`);
    }
    if (builderState.microphoneCompensation !== "") {
        tokens.push(`MC${builderState.microphoneCompensation}`);
    }
    if (builderState.freeFieldCompensation !== "") {
        tokens.push(`FF${builderState.freeFieldCompensation}`);
    }

    tokens.push(`XXu${builderState.waveRecordingMode}`);

    if (builderState.waveRecordingMode === "0") {
        return tokens;
    }

    tokens.push(`XXm${builderState.format}`);
    tokens.push(`XXn${builderState.bitsPerSample}`);
    tokens.push(`XXy${builderState.recordingTime}`);
    tokens.push(`XXo${builderState.recordingFilter}`);
    tokens.push(`XXl${builderState.samplingRate}`);
    tokens.push(`XXx${builderState.gain}`);
    tokens.push(`XXq${builderState.lengthLimitMode === "4gb" ? 0 : (builderState.lengthLimitMinutes ?? 0)}`);

    if (["2", "3", "4", "5", "6"].includes(builderState.waveRecordingMode)) {
        tokens.push(`XXY${builderState.triggerPeriod}`);
    }

    if (builderState.waveRecordingMode === "4" || builderState.waveRecordingMode === "5") {
        tokens.push(`XXz${builderState.preTrigger}`);
        tokens.push(`XXv${builderState.triggerLevel}`);
    }

    if (builderState.waveRecordingMode === "2" || builderState.waveRecordingMode === "3" || builderState.waveRecordingMode === "6") {
        tokens.push(`XXz${builderState.preTrigger}`);
        tokens.push(`XXZ${builderState.triggerGradient}`);
    }

    return tokens;
}
